import { useMemo, useState, useEffect } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import {
  DashboardQueryBundle,
  fetchDashboardQueryBundle,
} from '../api';
import { DashboardData } from '../types';
import { hasFilledNonComplianceReasons } from '../components/dashboard/utils/dataProcessing';
import {
  normalizeRawDataDate,
} from '../components/dashboard/utils/timeSeriesProcessing';

export interface DashboardFilters {
  dateRange: { start?: string; end?: string };
  departments: string[];
  occupations: string[];
  timings: string[];
  reasons: string[];
}

export interface DashboardFilterOptions {
  departments: string[];
  occupations: string[];
  timings: string[];
  reasons: string[];
}

const initialFilters: DashboardFilters = {
  dateRange: { start: undefined, end: undefined },
  departments: [],
  occupations: [],
  timings: [],
  reasons: [],
};

const toDashboardData = (apiData?: DashboardQueryBundle): DashboardData | undefined => {
  if (!apiData) return undefined;
  const nonCompliance = apiData.aggregates.results.filter(
    row => row.result === '実施しなかった',
  );
  const resultRows = apiData.aggregates.results
    .filter(row => row.result !== '実施しなかった')
    .concat(apiData.aggregates.reasons.length === 0 ? nonCompliance : [])
    .map((row, index) => ({
      id: `aggregate-result-${index}`,
      date: '',
      department: row.group.department,
      occupation: row.group.occupation,
      timing: row.group.timing,
      result: row.result ?? '',
      count: row.count,
    }));
  const reasonRows = apiData.aggregates.reasons.map((row, index) => ({
    id: `aggregate-reason-${index}`,
    date: '',
    department: row.group.department,
    occupation: row.group.occupation,
    timing: row.group.timing,
    result: '実施しなかった',
    reason: row.reason,
    count: row.count,
  }));
  const timeSeriesRows = apiData.timeseries.series.map((row, index) => ({
    id: `timeseries-${index}`,
    date: row.bucket,
    department: '',
    occupation: row.occupation,
    timing: '',
    result: row.result,
    reason: row.reason,
    count: row.count,
  }));
  return {
    raw_data: [...resultRows, ...reasonRows],
    time_series_data: timeSeriesRows,
    total_records: apiData.aggregates.total,
    occupation_stats: [], occupation_result_stats: [],
    timing_stats: [], timing_result_stats: [],
    summary_stats: {
      firstDate: apiData.aggregates.firstDate,
      lastDate: apiData.aggregates.lastDate,
      uniqueDays: apiData.aggregates.uniqueDays,
      uniqueDepartments: apiData.aggregates.uniqueDepartments,
    },
  };
};


export const useDashboardData = (fileId: string | undefined) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [filters, setFilters] = useState<DashboardFilters>(initialFilters);

  const {
    data: dashboardApiData,
    isLoading,
    isFetching,
    error,
    refetch,
  }: UseQueryResult<DashboardQueryBundle, Error> = useQuery({
    queryKey: ['dashboardData', fileId, filters],
    queryFn: () => fetchDashboardQueryBundle(fileId!, filters),
    enabled: !!fileId,
  });

  const dashboardData = useMemo(
    () => toDashboardData(dashboardApiData),
    [dashboardApiData]
  );

  const filterOptions: DashboardFilterOptions = useMemo(() => {
    if (!dashboardApiData) {
      return { departments: [], occupations: [], timings: [], reasons: [] };
    }
    return dashboardApiData.summary.filterOptions;
  }, [dashboardApiData]);

  const filteredDashboardData = useMemo(
    () => dashboardData,
    [dashboardData]
  );

  useEffect(() => {
    setFilters(initialFilters);
  }, [fileId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const hasReasonData = useMemo(
    () => hasFilledNonComplianceReasons(filteredDashboardData?.raw_data || []),
    [filteredDashboardData]
  );

  const hasTimeSeriesData = useMemo(() => {
    return (filteredDashboardData?.time_series_data || []).some(record => !!normalizeRawDataDate(record.date));
  }, [filteredDashboardData]);

  useEffect(() => {
    if (!hasReasonData && activeTab === 'reason') {
      setActiveTab('overview');
    }
  }, [hasReasonData, activeTab]);

  useEffect(() => {
    if (!hasTimeSeriesData && activeTab === 'timeseries') {
      setActiveTab('overview');
    }
  }, [hasTimeSeriesData, activeTab]);

  const handleFiltersChange = (updated: Partial<DashboardFilters>) => {
    setFilters(prev => ({ ...prev, ...updated }));
  };

  const resetFilters = () => setFilters(initialFilters);

  return {
    dashboardData,
    filteredDashboardData,
    filterOptions,
    filters,
    isLoading,
    isFetching,
    error,
    refetch,
    activeTab,
    handleTabChange,
    setActiveTab,
    hasReasonData,
    hasTimeSeriesData,
    handleFiltersChange,
    resetFilters,
    fileName: dashboardApiData?.summary.filename,
  };
};
